import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { usersAPI, companiesAPI, depotsAPI } from '../services/api';
import DataTable from '../components/DataTable';
import { useAuthStore } from '../store/authStore';
import { hasAtLeastPermission } from '../utils/moduleAccess';

// Helper to format API errors, including Pydantic 422 validation errors
const parseApiError = (error) => {
  const detail = error?.response?.data?.detail;
  if (!detail) return error?.message || 'Error saving user';

  // Pydantic returns a list of error dicts
  if (Array.isArray(detail)) {
    try {
      return detail.map((d) => {
        if (d.loc && d.msg) {
          const field = Array.isArray(d.loc) ? d.loc.slice(-1)[0] : d.loc;
          return `${field}: ${d.msg}`;
        }
        return d.msg || JSON.stringify(d);
      }).join('; ');
    } catch (e) {
      return JSON.stringify(detail);
    }
  }

  // If detail is an object mapping fields to messages
  if (typeof detail === 'object') {
    try {
      return Object.entries(detail).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('; ');
    } catch (e) {
      return JSON.stringify(detail);
    }
  }

  // Fallback string
  return String(detail);
};

export default function Users() {
  const { user: currentUser } = useAuthStore();
  const permissions = useAuthStore((state) => state.permissions);
  const isAdminLike = currentUser?.user_type === 'gods_eye' || currentUser?.user_type === 'manager';
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // Initialize formData with current user's company if available
  const [formData, setFormData] = useState({
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    cedula: '',
    address: '',
    password: '',
    user_type: 'cliente',
    company_id: currentUser?.company_id || 1,
    depot_id: null,
  });

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
    fetchDepots();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      toast.error('Error fetching users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await companiesAPI.getPublic();
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Error fetching companies');
    }
  };

  const fetchDepots = async () => {
    try {
      const response = await depotsAPI.getPublic();
      setDepots(response.data);
    } catch (error) {
      console.error('Error fetching depots:', error);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    const canEdit = isAdminLike || hasAtLeastPermission(permissions, 'users', 'edit');
    if (!canEdit) {
      toast.error('You do not have permission to create or edit users');
      return;
    }
    try {
      // Validate company_id is set
      if (!formData.company_id || formData.company_id === 0) {
        toast.error('Please select a company');
        return;
      }
      
      // Validate depot_id for operador users
      if (formData.user_type === 'operador' && !formData.depot_id) {
        toast.error('Please select a depot for operador users');
        return;
      }
      
      // Prepare payload - don't send password if empty for updates
      const payload = { ...formData };
      if (editingId && !payload.password) {
        delete payload.password;
      }
      
      // If not operador, clear depot_id
      if (formData.user_type !== 'operador') {
        payload.depot_id = null;
      }
      
      if (editingId) {
        console.log('Updating user with payload:', payload);
        await usersAPI.update(editingId, payload);
        toast.success('User updated successfully');
        setEditingId(null);
      } else {
        console.log('Creating user with payload:', payload);
        await usersAPI.create(payload);
        toast.success('User created successfully');
      }
      
      const defaultCompanyId = currentUser?.company_id || (companies.length > 0 ? companies[0].id : 1);
      setFormData({
        username: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        cedula: '',
        address: '',
        password: '',
        user_type: 'cliente',
        company_id: defaultCompanyId,
        depot_id: null,
      });
      setShowForm(false);
      fetchUsers();
    } catch (error) {
      const errorMsg = parseApiError(error);
      toast.error(errorMsg);
      console.error('Submit error:', error);
    }
  };

  const handleDelete = async (user) => {
    const canDelete = isAdminLike || hasAtLeastPermission(permissions, 'users', 'full_access');
    if (!canDelete) {
      toast.error('You do not have permission to delete users');
      return;
    }
    try {
      await usersAPI.delete(user.id);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Error deleting user');
    }
  };

  const handlePasswordReset = async (user) => {
    const canEdit = isAdminLike || hasAtLeastPermission(permissions, 'users', 'edit');
    if (!canEdit) {
      toast.error('You do not have permission to reset passwords');
      return;
    }
    const newPassword = prompt(`Enter new password for ${user.username}:`, '');
    if (newPassword === null) {
      // User cancelled
      return;
    }
    
    if (newPassword.trim() === '') {
      toast.error('Password cannot be empty');
      return;
    }
    
    try {
      await usersAPI.resetPassword(user.id, newPassword);
      toast.success(`Password reset successfully for ${user.username}`);
      fetchUsers();
    } catch (error) {
      const errorMsg = parseApiError(error);
      toast.error(errorMsg);
      console.error('Password reset error:', error);
    }
  };

  const handleManagePermissions = (user) => {
    const canEdit = isAdminLike || hasAtLeastPermission(permissions, 'users', 'edit');
    if (!canEdit) {
      toast.error('You do not have permission to manage user permissions');
      return;
    }
    navigate(`/permissions?selectedUserId=${user.id}`);
  };

  const handleEdit = (user) => {
    const canEdit = isAdminLike || hasAtLeastPermission(permissions, 'users', 'edit');
    if (!canEdit) {
      toast.error('You do not have permission to edit users');
      return;
    }
    // Ensure we have the user's actual company_id from the data
    const companyId = user.company_id || currentUser?.company_id || (companies.length > 0 ? companies[0].id : 1);
    setFormData({ 
      ...user, 
      password: '',
      company_id: companyId
    });
    setEditingId(user.id);
    setShowForm(true);
  };

  const handleAddClick = () => {
    const canEdit = isAdminLike || hasAtLeastPermission(permissions, 'users', 'edit');
    if (!canEdit) {
      toast.error('You do not have permission to create users');
      return;
    }
    const defaultCompanyId = currentUser?.company_id || (companies.length > 0 ? companies[0].id : 1);
    setFormData({
      username: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      cedula: '',
      address: '',
      password: '',
      user_type: 'cliente',
      company_id: defaultCompanyId,
      depot_id: null,
    });
    setEditingId(null);
    setShowForm(true);
  };

  const getCompanyName = (companyId, user) => {
    // Try to get company name from user object first
    if (user?.company?.name) {
      return user.company.name;
    }
    
    // Fallback to companies array
    if (companyId) {
      const company = companies.find(c => c.id === companyId);
      if (company?.name) {
        return company.name;
      }
    }
    
    // Default fallback
    return 'N/A';
  };

  const getDepotName = (depotId, user) => {
    // Try to get depot name from user object first
    if (user?.depot?.name) {
      return user.depot.name;
    }
    
    // Fallback to depots array
    if (depotId) {
      const depot = depots.find(d => d.id === depotId);
      if (depot?.name) {
        return depot.name;
      }
    }
    
    // Return empty string for non-operador users
    return '-';
  };

  const columns = [
    { key: 'id', label: 'ID', width: '80px', sortable: true },
    { key: 'username', label: 'Username', sortable: true },
    { key: 'first_name', label: 'First Name', sortable: true },
    { key: 'last_name', label: 'Last Name', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'user_type', label: 'Type', sortable: true },
    {
      key: 'company_id',
      label: 'Company',
      render: (value, row) => getCompanyName(row.company_id, row),
      sortable: true
    },
    {
      key: 'depot_id',
      label: 'Depot',
      render: (value, row) => row.user_type === 'operador' ? getDepotName(row.depot_id, row) : '-',
      sortable: true
    },
  ];

  return (
    <div className="container">
      <h1 className="page-title">Users</h1>
      {(isAdminLike || hasAtLeastPermission(permissions, 'users', 'edit')) && (
        <button className="btn btn-primary" onClick={handleAddClick}>
          Add User
        </button>
      )}

      {showForm && (
        <div className="modal active">
          <div className="modal-content">
            <span className="close" onClick={() => { 
              setShowForm(false); 
              setEditingId(null);
              setFormData({
                username: '',
                first_name: '',
                last_name: '',
                email: '',
                phone: '',
                cedula: '',
                address: '',
                password: '',
                user_type: 'cliente',
                company_id: 1,
                depot_id: null,
              });
            }}>&times;</span>
            <h2>{editingId ? 'Edit User' : 'Add User'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  disabled={editingId !== null}
                />
              </div>
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Cedula</label>
                <input
                  type="text"
                  value={formData.cedula}
                  onChange={(e) => setFormData({ ...formData, cedula: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                />
              </div>
              {!editingId && (
                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingId}
                  />
                </div>
              )}
              <div className="form-group">
                <label>User Type</label>
                <select
                  value={formData.user_type}
                  onChange={(e) => setFormData({ ...formData, user_type: e.target.value })}
                >
                  <option value="cliente">Cliente</option>
                  <option value="operador">Operador</option>
                  <option value="verificador">Verificador</option>
                  <option value="distribuidor">Distribuidor</option>
                  {currentUser?.user_type === 'gods_eye' && (
                    <>
                      <option value="manager">Manager</option>
                      <option value="gods_eye">Gods Eye</option>
                    </>
                  )}
                </select>
                {currentUser?.user_type === 'manager' && (
                  <small style={{ color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>
                    You can only create Cliente, Operador, Verificador, and Distribuidor users
                  </small>
                )}
              </div>
              {formData.user_type === 'operador' && (
                <div className="form-group">
                  <label>Depot *</label>
                  <select
                    value={formData.depot_id || ''}
                    onChange={(e) => setFormData({ ...formData, depot_id: e.target.value ? parseInt(e.target.value) : null })}
                    required
                  >
                    <option value="">Select a depot</option>
                    {depots
                      .filter(depot => depot.company_id === parseInt(formData.company_id))
                      .map((depot) => (
                        <option key={depot.id} value={depot.id}>
                          {depot.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Company</label>
                <select
                  value={formData.company_id}
                  onChange={(e) => setFormData({ ...formData, company_id: parseInt(e.target.value) })}
                  disabled={currentUser?.user_type !== 'gods_eye'}
                >
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
                {currentUser?.user_type !== 'gods_eye' && (
                  <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                    Your company is automatically selected
                  </small>
                )}
              </div>
              <button type="submit" className="btn btn-success">
                {editingId ? 'Update' : 'Create'}
              </button>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          data={users}
          columns={columns}
          searchableFields={['username', 'email', 'first_name', 'last_name']}
          itemsPerPage={10}
          onEdit={handleEdit}
          onPasswordReset={handlePasswordReset}
          onDelete={handleDelete}
          onManagePermissions={handleManagePermissions}
          title="Users List"
        />
      )}
    </div>
  );
}
